import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Checkbox from '@repo/ui/checkbox'
import { BuildingIcon, CheckListTwoIcon, ContactIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { closeCatalog, getCse, openCatalog, updateCse } from '../../api/cse/cse'
import { createCseContact, updateCseContact } from '../../api/cse/cse-contacts'
import { createCseReductionRule, updateCseReductionRule } from '../../api/cse/cse-reduction-rules'
import { DynamicTableRef } from '../../components/elements/dynamic-table'
import InformationToolTip from '../../components/elements/information-tooltip'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import NestedDataTable from '../../components/elements/nested-table'
import CseContactForm from '../../components/pages/cse/contact/cse-contact-form'
import { cseContactTableColumns } from '../../components/pages/cse/contact/utils'
import CseForm from '../../components/pages/cse/cse-form'
import CseReductionForm from '../../components/pages/cse/reduction/cse-reduction-form'
import {
  cseReductionTableColumns,
  mappingCseReductionRuleToInput
} from '../../components/pages/cse/reduction/utils'
import { mapCseInputToRequest, mapCseToInput } from '../../components/pages/cse/utils'
import { Paths } from '../../lib/constants/path'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { ReductionTypeEnum } from '../../lib/types/enums/cse/reduction-type.enum'
import { CseContact } from '../../lib/types/models/cse/cse-contact'
import { CseReductionRule } from '../../lib/types/models/cse/cse-reduction-rule'
import {
  modalButtonConfig,
  updateButtonsConfig
} from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const CseUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  const contactTableRef = useRef<DynamicTableRef<CseContact>>(null)
  const reductionTableRef = useRef<DynamicTableRef<CseReductionRule>>(null)

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create-contact' | 'edit-contact',
    CseContact
  >(['create-contact', 'edit-contact'])

  const {
    selectedData: reductionSelectedData,
    modalState: reductionModalState,
    toggleModal: toggleReductionModal,
    closeModel: closeReductionModel
  } = useModalManager<'create-reduction' | 'edit-reduction', CseReductionRule>([
    'create-reduction',
    'edit-reduction'
  ])

  const { data: cse, isLoading } = useQuery({
    queryKey: ['cse', id],
    queryFn: () => {
      if (!id || isNaN(parseInt(id))) throw new Error('Identifiant de CSE invalide')
      return getCse(parseInt(id))
    }
  })

  const updateCseMutation = useMutation({
    mutationFn: updateCse,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['cse', id] })
      toast.success('CSE modifié avec succès')
    }
  })

  {
    /* --------------------Cse Contact Mutations-------------------- */
  }
  const createCseContactMutation = useMutation({
    mutationFn: createCseContact,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Contact ajouté avec succès')
      contactTableRef.current?.refetch()
      closeModel('create-contact')
    }
  })

  const updateCseContactMutation = useMutation({
    mutationFn: updateCseContact,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Contact modifié avec succès')
      contactTableRef.current?.refetch()
      closeModel('edit-contact')
    }
  })

  {
    /* --------------------Cse Reduction Mutations-------------------- */
  }
  const createCseReductionRuleMutation = useMutation({
    mutationFn: createCseReductionRule,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Réduction ajoutée avec succès')
      reductionTableRef.current?.refetch()
      closeReductionModel('create-reduction')
    }
  })

  const updateCseReductionRuleMutation = useMutation({
    mutationFn: updateCseReductionRule,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Réduction modifiée avec succès')
      reductionTableRef.current?.refetch()
      closeReductionModel('edit-reduction')
    }
  })

  {
    /* --------------------catalog Mutations-------------------- */
  }

  const closeCatalogMutation = useMutation({
    mutationFn: closeCatalog,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Catalogue fermé avec succès')
      queryClient.invalidateQueries({ exact: true, queryKey: ['cse', id] })
    }
  })

  const openCatalogMutation = useMutation({
    mutationFn: openCatalog,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Catalogue ouvert avec succès')
      queryClient.invalidateQueries({ exact: true, queryKey: ['cse', id] })
    }
  })

  if (isLoading) return <LoadingScreen />
  if (!cse) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier un CSE'} icon={BuildingIcon} />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <CseForm
          defaultValues={mapCseToInput(cse)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.CSE_LIST_PAGE),
            onUpdate: (values) =>
              updateCseMutation.mutateAsync({ id: cse.id, ...mapCseInputToRequest(values) }),
            onUpdateThenCreate: async (values) => {
              await updateCseMutation.mutateAsync({ id: cse.id, ...mapCseInputToRequest(values) })
              navigate(Paths.CSE_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await updateCseMutation.mutateAsync({ id: cse.id, ...mapCseInputToRequest(values) })
              back(Paths.CSE_LIST_PAGE)
            }
          })}
        />
        <div className="h-4" />

        {/* --------------------Contacts Section-------------------- */}
        <NestedDataTable<CseContact, null>
          ref={contactTableRef}
          title={'Contacts'}
          variant="action"
          deleteMessage={{
            name: 'contacts',
            attributeName: 'firstName',
            deleteOne: (cseContact) =>
              `Êtes-vous sûr de vouloir supprimer le contact ${StringFormatter.capitalize(cseContact.firstName)} ${cseContact.lastName.toUpperCase()} ?`
          }}
          onDelete={() => toast.success('Contact supprimé avec succès')}
          columns={cseContactTableColumns}
          onEditAction={(cseContact) => toggleModal('edit-contact', cseContact)}
          endpoint={API.cse.getAllContacts.url.replace(':id', cse.id.toString())}
          actions={[
            {
              type: 'global',
              icon: PlusIcon,
              variant: 'outline',
              label: 'Ajouter un contact',
              fn: () => toggleModal('create-contact')
            }
          ]}
        />

        {/* --------------------Contact Modals-------------------- */}
        <Modal
          isOpen={modalState['create-contact']}
          title={'Ajouter un contact'}
          icon={ContactIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('create-contact')}
        >
          <CseContactForm
            defaultValues={{ canReceiveEmails: false }}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create-contact'),
              isLoading: createCseContactMutation.isPending,
              onConfirm: (values) =>
                createCseContactMutation.mutateAsync({ id: cse.id, ...values })
            })}
          />
        </Modal>
        {selectedData && (
          <Modal
            isOpen={modalState['edit-contact']}
            title={'Modifier un contact'}
            icon={ContactIcon}
            hideActions
            size="medium"
            onCancel={() => closeModel('edit-contact')}
          >
            <CseContactForm
              className={MODAL_CONTENT_CLASSNAME}
              defaultValues={selectedData}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit-contact'),
                isLoading: updateCseContactMutation.isPending,
                onConfirm: (values) =>
                  updateCseContactMutation.mutateAsync({
                    contactId: selectedData.id,
                    ...values,
                    id: cse.id
                  })
              })}
            />
          </Modal>
        )}

        {/* --------------------Reduction Rules Section-------------------- */}
        <NestedDataTable<CseReductionRule, null>
          ref={reductionTableRef}
          title={'Règles de réduction'}
          variant="action"
          widget={
            <div className="flex items-center gap-2 w-fit p-2 rounded-sm bg-white-hover border-grey-light">
              <Checkbox
                checked={cse.closedCatalog}
                onChange={(checked) => {
                  if (checked) closeCatalogMutation.mutateAsync(cse.id)
                  else openCatalogMutation.mutateAsync(cse.id)
                }}
              />
              <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
                Catalogue fermé
              </Typography>
              <InformationToolTip
                align="start"
                variant="warning"
                contentVariant="default"
                size="small"
              >
                <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
                  En cochant cette case, seuls les séjours ci-dessous
                  <br />
                  apparaîtront dans le moteur de recherche du site
                </Typography>
              </InformationToolTip>
            </div>
          }
          deleteMessage={{
            name: 'réductions',
            attributeName: 'name',
            deleteOne: (cseReductionRule) =>
              `Êtes-vous sûr de vouloir supprimer la règle de réduction ${cseReductionRule.name} ?`
          }}
          onDelete={() => toast.success('Contact supprimé avec succès')}
          columns={cseReductionTableColumns}
          onEditAction={(cseReductionRule) => {
            console.log(cseReductionRule)
            toggleReductionModal('edit-reduction', cseReductionRule)
          }}
          endpoint={API.cse.getAllReductionRules.url.replace(':id', cse.id.toString())}
          actions={[
            {
              type: 'global',
              icon: PlusIcon,
              variant: 'outline',
              label: 'Ajouter une réduction',
              fn: () => toggleReductionModal('create-reduction')
            }
          ]}
        />

        {/* --------------------Reduction Rule Modals-------------------- */}
        <Modal
          isOpen={reductionModalState['create-reduction']}
          title={'Ajouter une réduction'}
          icon={CheckListTwoIcon}
          size="medium"
          hideActions
          onCancel={() => closeReductionModel('create-reduction')}
        >
          <CseReductionForm
            defaultValues={{ type: ReductionTypeEnum.PERCENTAGE }}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeReductionModel('create-reduction'),
              isLoading: createCseReductionRuleMutation.isPending,
              onConfirm: (values) =>
                createCseReductionRuleMutation.mutateAsync({ id: cse.id, ...values })
            })}
          />
        </Modal>
        {reductionSelectedData && (
          <Modal
            isOpen={reductionModalState['edit-reduction']}
            title={'Modifier une réduction'}
            icon={ContactIcon}
            hideActions
            size="medium"
            onCancel={() => closeReductionModel('edit-reduction')}
          >
            <CseReductionForm
              className={MODAL_CONTENT_CLASSNAME}
              defaultValues={mappingCseReductionRuleToInput(reductionSelectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeReductionModel('edit-reduction'),
                isLoading: updateCseReductionRuleMutation.isPending,
                onConfirm: (values) =>
                  updateCseReductionRuleMutation.mutateAsync({
                    ruleId: reductionSelectedData.id,
                    ...values,
                    id: cse.id
                  })
              })}
            />
          </Modal>
        )}
      </Box>
    </div>
  )
}

export default CseUpdatePage
